// Cinematic Experience — Pikuizee's 3-act (Clarity / Energy / Balance)
// generative background system. WebGL2 (via Three.js, loaded as an ES
// module from a CDN since this theme has no build step), driven entirely
// by scroll through a single uMorph uniform (0..2). Never a hard cut: every
// scene property (background shader, particle field, camera position,
// vial tilt) is lerped toward its target every frame instead of jumping.
//
// Brand palette (see Pikuizee_Mobile/src/theme.ts — same brand system,
// mirrored here since this Shopify theme has no shared token source):
//   Warm White #fdfaf5, Sand #e8dfd0, Sage #7a9471, Moss #3d4f3a,
//   Charcoal #2a2a25. "Stone" has no canonical hex anywhere in either
//   codebase — #a89a86 below is a derived warm neutral, not an existing
//   brand value; adjust if there's a real one.
//
// Gracefully does nothing if WebGL2 isn't available (old device/browser) —
// the section's DOM content (headings, vials) still renders and scrolls
// normally, just without the animated background.
import * as THREE from "https://cdn.jsdelivr.net/npm/three@0.180.0/build/three.module.js";

const PALETTE = {
  warmWhite: 0xfdfaf5,
  sand: 0xe8dfd0,
  sage: 0x7a9471,
  moss: 0x3d4f3a,
  charcoal: 0x2a2a25,
  stone: 0xa89a86,
};

const GOLDEN_ANGLE = Math.PI * (3 - Math.sqrt(5)); // ~137.5°
const PARTICLE_COUNT = 1200; // was 2200 — trimmed for performance

// ---------------------------------------------------------------------
// Shaders
// ---------------------------------------------------------------------

const BG_VERTEX = /* glsl */ `
  varying vec2 vUv;
  void main() {
    vUv = uv;
    gl_Position = vec4(position, 1.0);
  }
`;

// Ashima Arts' classic 3D simplex noise (MIT) — the standard, battle-tested
// GLSL noise implementation reused across the WebGL community. Everything
// downstream (fBm, domain warp, curl noise in the particle shader) is built
// on this one primitive.
const SIMPLEX_3D = /* glsl */ `
  vec3 mod289(vec3 x) { return x - floor(x * (1.0 / 289.0)) * 289.0; }
  vec4 mod289(vec4 x) { return x - floor(x * (1.0 / 289.0)) * 289.0; }
  vec4 permute(vec4 x) { return mod289(((x * 34.0) + 1.0) * x); }
  vec4 taylorInvSqrt(vec4 r) { return 1.79284291400159 - 0.85373472095314 * r; }

  float snoise(vec3 v) {
    const vec2 C = vec2(1.0 / 6.0, 1.0 / 3.0);
    const vec4 D = vec4(0.0, 0.5, 1.0, 2.0);

    vec3 i  = floor(v + dot(v, C.yyy));
    vec3 x0 = v - i + dot(i, C.xxx);

    vec3 g = step(x0.yzx, x0.xyz);
    vec3 l = 1.0 - g;
    vec3 i1 = min(g.xyz, l.zxy);
    vec3 i2 = max(g.xyz, l.zxy);

    vec3 x1 = x0 - i1 + C.xxx;
    vec3 x2 = x0 - i2 + C.yyy;
    vec3 x3 = x0 - D.yyy;

    i = mod289(i);
    vec4 p = permute(permute(permute(
              i.z + vec4(0.0, i1.z, i2.z, 1.0))
            + i.y + vec4(0.0, i1.y, i2.y, 1.0))
            + i.x + vec4(0.0, i1.x, i2.x, 1.0));

    float n_ = 0.142857142857;
    vec3 ns = n_ * D.wyz - D.xzx;

    vec4 j = p - 49.0 * floor(p * ns.z * ns.z);

    vec4 x_ = floor(j * ns.z);
    vec4 y_ = floor(j - 7.0 * x_);

    vec4 x = x_ * ns.x + ns.yyyy;
    vec4 y = y_ * ns.x + ns.yyyy;
    vec4 h = 1.0 - abs(x) - abs(y);

    vec4 b0 = vec4(x.xy, y.xy);
    vec4 b1 = vec4(x.zw, y.zw);

    vec4 s0 = floor(b0) * 2.0 + 1.0;
    vec4 s1 = floor(b1) * 2.0 + 1.0;
    vec4 sh = -step(h, vec4(0.0));

    vec4 a0 = b0.xzyw + s0.xzyw * sh.xxyy;
    vec4 a1 = b1.xzyw + s1.xzyw * sh.zzww;

    vec3 p0 = vec3(a0.xy, h.x);
    vec3 p1 = vec3(a0.zw, h.y);
    vec3 p2 = vec3(a1.xy, h.z);
    vec3 p3 = vec3(a1.zw, h.w);

    vec4 norm = taylorInvSqrt(vec4(dot(p0, p0), dot(p1, p1), dot(p2, p2), dot(p3, p3)));
    p0 *= norm.x; p1 *= norm.y; p2 *= norm.z; p3 *= norm.w;

    vec4 m = max(0.6 - vec4(dot(x0, x0), dot(x1, x1), dot(x2, x2), dot(x3, x3)), 0.0);
    m = m * m;
    return 42.0 * dot(m * m, vec4(dot(p0, x0), dot(p1, x1), dot(p2, x2), dot(p3, x3)));
  }
`;

const BG_FRAGMENT = /* glsl */ `
  precision highp float;
  varying vec2 vUv;

  uniform float uTime;
  uniform float uMorph;      // 0 = Clarity, 1 = Energy, 2 = Balance
  uniform vec2 uResolution;
  uniform vec3 uColorWarmWhite;
  uniform vec3 uColorSand;
  uniform vec3 uColorSage;
  uniform vec3 uColorMoss;
  uniform vec3 uColorCharcoal;
  uniform vec3 uColorStone;

  ${SIMPLEX_3D}

  // Fractal Brownian motion — 3 octaves of simplex noise. (Dropped from 5:
  // warpedFbm below calls this 4x per pixel, so every octave removed here
  // is a large multiplier on total shader cost — 3 is still plenty of
  // detail for a background flow field seen through a vignette/overlay.)
  float fbm(vec3 p) {
    float sum = 0.0;
    float amp = 0.5;
    float freq = 1.0;
    for (int i = 0; i < 3; i++) {
      sum += amp * snoise(p * freq);
      freq *= 2.02;
      amp *= 0.5;
    }
    return sum;
  }

  // Domain-warped fBm — the classic "fbm(p + fbm(p))" technique (Iñigo
  // Quilez), simplified to a single warp level (q) instead of the fuller
  // 2-level (q then r) version for performance — still reads as organic,
  // flowing structure, just cheaper (4 fbm calls/pixel instead of 7).
  float warpedFbm(vec3 p) {
    vec3 q = vec3(
      fbm(p + vec3(0.0, 0.0, uTime * 0.03)),
      fbm(p + vec3(5.2, 1.3, uTime * 0.025)),
      fbm(p + vec3(1.7, 9.2, uTime * 0.02))
    );
    return fbm(p + 4.0 * q);
  }

  // Voronoi cells (F1 distance to nearest feature point) — cellular
  // structure layered on top of the fBm flow field.
  vec2 voronoi(vec2 x) {
    vec2 n = floor(x);
    vec2 f = fract(x);
    float minDist = 8.0;
    vec2 minPoint;
    for (int j = -1; j <= 1; j++) {
      for (int i = -1; i <= 1; i++) {
        vec2 neighbor = vec2(float(i), float(j));
        vec2 point = 0.5 + 0.5 * sin(uTime * 0.05 + 6.2831 * snoise(vec3(n + neighbor, 0.0)) * vec2(1.0, 1.3));
        vec2 diff = neighbor + point - f;
        float dist = length(diff);
        if (dist < minDist) {
          minDist = dist;
          minPoint = point;
        }
      }
    }
    return vec2(minDist, snoise(vec3(n + minPoint, 1.0)));
  }

  // Smooth minimum (Iñigo Quilez, polynomial variant) — blends two SDF
  // fields into one continuous, organic surface instead of a hard union.
  float smin(float a, float b, float k) {
    float h = clamp(0.5 + 0.5 * (b - a) / k, 0.0, 1.0);
    return mix(b, a, h) - k * h * (1.0 - h);
  }

  float sdSphere(vec3 p, float r) {
    return length(p) - r;
  }

  // Raymarched blob: 5 spheres orbiting the origin at the golden angle
  // from one another, unioned with smooth-min so they read as one
  // continuous, breathing organic form rather than 5 separate balls.
  float sceneSDF(vec3 p) {
    float d = 1e5;
    const float GOLDEN_ANGLE = 2.399963229728653;
    for (int i = 0; i < 5; i++) {
      float fi = float(i);
      float angle = fi * GOLDEN_ANGLE + uTime * 0.15 + uMorph * 1.4;
      float orbitRadius = 0.55 + 0.12 * sin(uTime * 0.2 + fi * 1.7);
      float height = sin(fi * 1.9 + uTime * 0.12 + uMorph) * 0.35;
      vec3 center = vec3(cos(angle) * orbitRadius, height, sin(angle) * orbitRadius);
      float r = 0.32 + 0.08 * sin(uTime * 0.3 + fi);
      d = smin(d, sdSphere(p - center, r), 0.45);
    }
    return d;
  }

  vec3 sceneNormal(vec3 p) {
    float e = 0.001;
    return normalize(vec3(
      sceneSDF(p + vec3(e, 0.0, 0.0)) - sceneSDF(p - vec3(e, 0.0, 0.0)),
      sceneSDF(p + vec3(0.0, e, 0.0)) - sceneSDF(p - vec3(0.0, e, 0.0)),
      sceneSDF(p + vec3(0.0, 0.0, e)) - sceneSDF(p - vec3(0.0, 0.0, e))
    ));
  }

  // Raymarch the blob from a camera looking down -Z at the origin. 36
  // steps (down from 64) with a looser hit threshold — the blob's
  // silhouette is soft/backlit in the final composite, so the extra
  // precision wasn't visually paying for its cost.
  vec4 raymarchBlob(vec3 ro, vec3 rd) {
    float t = 0.0;
    for (int i = 0; i < 36; i++) {
      vec3 p = ro + rd * t;
      float d = sceneSDF(p);
      if (d < 0.004) {
        vec3 n = sceneNormal(p);
        vec3 lightDir = normalize(vec3(0.5, 0.8, 0.6));
        float diffuse = max(dot(n, lightDir), 0.0);
        float fresnel = pow(1.0 - max(dot(n, -rd), 0.0), 2.5);
        return vec4(n, diffuse + fresnel * 0.6);
      }
      t += d;
      if (t > 6.0) break;
    }
    return vec4(0.0);
  }

  void main() {
    vec2 uv = vUv;
    vec2 p = (uv - 0.5) * vec2(uResolution.x / uResolution.y, 1.0);

    // ---- Act palette: 0=Clarity (warm/bright), 1=Energy (sage/vivid),
    // 2=Balance (moss/deep). uMorph lerps continuously across both
    // transitions so it's always mid-blend, never a jump cut.
    vec3 clarityColor = mix(uColorWarmWhite, uColorSand, 0.5);
    vec3 energyColor = mix(uColorSage, uColorStone, 0.45);
    vec3 balanceColor = mix(uColorMoss, uColorCharcoal, 0.55);

    vec3 baseColor;
    if (uMorph < 1.0) {
      baseColor = mix(clarityColor, energyColor, smoothstep(0.0, 1.0, uMorph));
    } else {
      baseColor = mix(energyColor, balanceColor, smoothstep(0.0, 1.0, uMorph - 1.0));
    }

    // ---- Domain-warped fBm flow field.
    float flow = warpedFbm(vec3(p * 1.6, uTime * 0.04 + uMorph * 0.6));

    // ---- Voronoi cellular layer.
    vec2 vor = voronoi(p * 3.2 + uTime * 0.02);
    float cells = smoothstep(0.0, 0.08, vor.x);

    // ---- Raymarched blob, camera slowly orbiting.
    float camAngle = uTime * 0.06 + uMorph * 0.3;
    vec3 ro = vec3(sin(camAngle) * 2.4, 0.3, cos(camAngle) * 2.4);
    vec3 target = vec3(0.0);
    vec3 fwd = normalize(target - ro);
    vec3 right = normalize(cross(fwd, vec3(0.0, 1.0, 0.0)));
    vec3 up = cross(right, fwd);
    vec3 rd = normalize(fwd * 1.8 + right * p.x + up * p.y);
    vec4 blob = raymarchBlob(ro, rd);

    // ---- Composite: base gradient + flow tint + voronoi cell shading + blob.
    vec3 color = baseColor;
    color = mix(color, mix(baseColor, uColorStone, 0.35), flow * 0.5 + 0.5);
    color *= mix(0.92, 1.06, cells);

    if (blob.a > 0.0) {
      vec3 blobColor = mix(uColorSage, uColorMoss, uMorph * 0.5);
      color = mix(color, blobColor, clamp(blob.a, 0.0, 1.0) * 0.85);
    }

    // Gentle vignette so content overlaid on top stays readable.
    float vignette = smoothstep(1.1, 0.25, length(p));
    color = mix(color * 0.82, color, vignette);

    gl_FragColor = vec4(color, 1.0);
  }
`;

const PARTICLE_VERTEX = /* glsl */ `
  precision highp float;
  attribute float aSeed;
  uniform float uTime;
  uniform float uMorph;
  uniform vec2 uMouse;      // normalized device coords, [-1, 1]
  uniform float uPixelRatio;

  ${SIMPLEX_3D}

  // Curl of a 3-component vector potential built from simplex noise —
  // divergence-free by construction, which is what gives curl-noise its
  // characteristic swirling (never converging/diverging) flow.
  vec3 curlNoise(vec3 p) {
    const float e = 0.1;
    vec3 dx = vec3(e, 0.0, 0.0);
    vec3 dy = vec3(0.0, e, 0.0);
    vec3 dz = vec3(0.0, 0.0, e);

    float x0 = snoise(p - dx + vec3(0.0, 31.4, 0.0));
    float x1 = snoise(p + dx + vec3(0.0, 31.4, 0.0));
    float y0 = snoise(p - dy + vec3(17.3, 0.0, 0.0));
    float y1 = snoise(p + dy + vec3(17.3, 0.0, 0.0));
    float z0 = snoise(p - dz + vec3(0.0, 0.0, 5.7));
    float z1 = snoise(p + dz + vec3(0.0, 0.0, 5.7));

    float px0 = snoise(p - dx + vec3(9.1, 0.0, 21.5));
    float px1 = snoise(p + dx + vec3(9.1, 0.0, 21.5));
    float py0 = snoise(p - dy + vec3(0.0, 9.1, 21.5));
    float py1 = snoise(p + dy + vec3(0.0, 9.1, 21.5));

    float qx0 = snoise(p - dx + vec3(3.3, 41.2, 0.0));
    float qx1 = snoise(p + dx + vec3(3.3, 41.2, 0.0));
    float qz0 = snoise(p - dz + vec3(3.3, 0.0, 41.2));
    float qz1 = snoise(p + dz + vec3(3.3, 0.0, 41.2));

    float curlX = ((y1 - y0) - (qz1 - qz0)) / (2.0 * e);
    float curlY = ((qx1 - qx0) - (x1 - x0)) / (2.0 * e);
    float curlZ = ((px1 - px0) - (py1 - py0)) / (2.0 * e);

    return vec3(curlX, curlY, curlZ);
  }

  void main() {
    vec3 pos = position;

    // Curl-noise drift, slowly evolving over time and nudged by uMorph so
    // the field visibly reorganizes between acts instead of just looping.
    vec3 flow = curlNoise(pos * 1.3 + vec3(0.0, 0.0, uTime * 0.06) + uMorph * 3.1);
    pos += flow * 0.35;

    // Mouse repulsion — particles within range get pushed radially away.
    vec2 toMouse = pos.xy - uMouse * 1.6;
    float distToMouse = length(toMouse);
    float repel = smoothstep(0.6, 0.0, distToMouse);
    pos.xy += normalize(toMouse + 0.0001) * repel * 0.4;

    vec4 mvPosition = modelViewMatrix * vec4(pos, 1.0);
    gl_Position = projectionMatrix * mvPosition;

    float sizePulse = 0.6 + 0.4 * sin(uTime * 1.5 + aSeed * 6.2831);
    gl_PointSize = (2.2 + sizePulse * 1.8) * uPixelRatio * (1.0 / -mvPosition.z);
  }
`;

const PARTICLE_FRAGMENT = /* glsl */ `
  precision highp float;
  uniform vec3 uColorSage;
  uniform vec3 uColorWarmWhite;
  uniform float uMorph;

  void main() {
    vec2 c = gl_PointCoord - 0.5;
    float d = length(c);
    if (d > 0.5) discard;
    float alpha = smoothstep(0.5, 0.0, d);
    vec3 color = mix(uColorWarmWhite, uColorSage, clamp(uMorph * 0.5, 0.0, 1.0));
    gl_FragColor = vec4(color, alpha * 0.85);
  }
`;

// ---------------------------------------------------------------------
// Particle geometry — Fibonacci spiral (golden-ratio) distribution over a
// sphere, the same construction used to place N points as evenly as
// possible on a sphere's surface with no clustering at the poles.
// ---------------------------------------------------------------------
function buildFibonacciSpherePositions(count, radius) {
  const positions = new Float32Array(count * 3);
  const seeds = new Float32Array(count);
  for (let i = 0; i < count; i++) {
    const y = 1 - (i / (count - 1)) * 2;
    const radiusAtY = Math.sqrt(1 - y * y);
    const theta = GOLDEN_ANGLE * i;
    const x = Math.cos(theta) * radiusAtY;
    const z = Math.sin(theta) * radiusAtY;
    positions[i * 3] = x * radius;
    positions[i * 3 + 1] = y * radius;
    positions[i * 3 + 2] = z * radius;
    seeds[i] = Math.random();
  }
  return { positions, seeds };
}

function colorUniform(hex) {
  return { value: new THREE.Color(hex) };
}

// The fragment shader's cost scales with total pixel count (width × height
// × pixelRatio), and a desktop browser window is very often several times
// more total pixels than a phone screen even though the phone's GPU is
// weaker per-pixel — which is exactly why this looked heavy on web but
// fine on mobile. Reported here as a viewport-width-based cap on top of
// devicePixelRatio, kept unchanged for phone/tablet widths (already
// running well) and pulled down harder the wider the viewport gets.
function computeEffectivePixelRatio() {
  const dpr = window.devicePixelRatio || 1;
  const width = window.innerWidth;
  if (width >= 1400) return Math.min(dpr, 0.85);
  if (width >= 900) return Math.min(dpr, 1.1);
  return Math.min(dpr, 1.5);
}

class CinematicExperience {
  constructor(root) {
    this.root = root;
    this.canvas = root.querySelector("[data-cinematic-canvas]");
    this.acts = Array.from(root.querySelectorAll("[data-cinematic-act]"));
    this.vials = Array.from(root.querySelectorAll("[data-cinematic-vial]"));
    this.mouse = { x: 0, y: 0 };
    this.targetMorph = 0;
    this.morph = 0;
    this.destroyed = false;

    if (!this.canvas || !window.WebGL2RenderingContext) return;

    try {
      this.setUpScene();
    } catch (err) {
      // Fails closed: leave the DOM content (headings/vials) visible and
      // scrollable, just without the animated background, rather than
      // breaking the section over a shader/WebGL error.
      console.error("Cinematic experience failed to initialize:", err);
      return;
    }

    this.bindEvents();
    this.setUpScrollMorph();
    this.clock = new THREE.Clock();
    this.animate();
  }

  setUpScene() {
    const renderer = new THREE.WebGLRenderer({
      canvas: this.canvas,
      // Off on purpose: this is a full-screen procedural shader with no
      // hard geometric edges to smooth (the blob's silhouette is already
      // soft/backlit) — MSAA here just multiplies fragment-shader cost for
      // no visible benefit, and that cost scales with total pixel count,
      // which is exactly what's expensive on a wide desktop viewport.
      antialias: false,
      alpha: false,
      powerPreference: "high-performance",
    });
    renderer.setPixelRatio(computeEffectivePixelRatio());
    renderer.setSize(window.innerWidth, window.innerHeight, false);
    this.renderer = renderer;

    this.scene = new THREE.Scene();
    this.camera = new THREE.PerspectiveCamera(50, window.innerWidth / window.innerHeight, 0.1, 100);
    this.camera.position.set(0, 0, 3);
    this.cameraTarget = new THREE.Vector3(0, 0, 3);
    this.cameraLookTarget = new THREE.Vector3(0, 0, 0);

    const uniforms = {
      uTime: { value: 0 },
      uMorph: { value: 0 },
      uResolution: { value: new THREE.Vector2(window.innerWidth, window.innerHeight) },
      uColorWarmWhite: colorUniform(PALETTE.warmWhite),
      uColorSand: colorUniform(PALETTE.sand),
      uColorSage: colorUniform(PALETTE.sage),
      uColorMoss: colorUniform(PALETTE.moss),
      uColorCharcoal: colorUniform(PALETTE.charcoal),
      uColorStone: colorUniform(PALETTE.stone),
    };
    this.bgUniforms = uniforms;

    const bgMaterial = new THREE.ShaderMaterial({
      vertexShader: BG_VERTEX,
      fragmentShader: BG_FRAGMENT,
      uniforms,
      depthWrite: false,
      depthTest: false,
    });
    const bgQuad = new THREE.Mesh(new THREE.PlaneGeometry(2, 2), bgMaterial);
    bgQuad.frustumCulled = false;
    bgQuad.renderOrder = -1;
    this.bgScene = new THREE.Scene();
    this.bgCamera = new THREE.Camera(); // full-screen quad needs no real projection
    this.bgScene.add(bgQuad);

    const { positions, seeds } = buildFibonacciSpherePositions(PARTICLE_COUNT, 1.4);
    const particleGeometry = new THREE.BufferGeometry();
    particleGeometry.setAttribute("position", new THREE.BufferAttribute(positions, 3));
    particleGeometry.setAttribute("aSeed", new THREE.BufferAttribute(seeds, 1));

    this.particleUniforms = {
      uTime: { value: 0 },
      uMorph: { value: 0 },
      uMouse: { value: new THREE.Vector2(0, 0) },
      uPixelRatio: { value: renderer.getPixelRatio() },
      uColorSage: colorUniform(PALETTE.sage),
      uColorWarmWhite: colorUniform(PALETTE.warmWhite),
    };
    const particleMaterial = new THREE.ShaderMaterial({
      vertexShader: PARTICLE_VERTEX,
      fragmentShader: PARTICLE_FRAGMENT,
      uniforms: this.particleUniforms,
      transparent: true,
      depthWrite: false,
      blending: THREE.AdditiveBlending,
    });
    this.particles = new THREE.Points(particleGeometry, particleMaterial);
    this.scene.add(this.particles);
  }

  bindEvents() {
    this._onResize = () => this.handleResize();
    this._onMouseMove = (e) => this.handleMouseMove(e);
    window.addEventListener("resize", this._onResize);
    window.addEventListener("mousemove", this._onMouseMove, { passive: true });

    this.vials.forEach((vial) => {
      // Raw mousemove can fire well over 60 times/sec — writing a style
      // property on every single one (each preceded by a layout-forcing
      // getBoundingClientRect) competed with the WebGL render loop for
      // frame time. Coalescing to "latest position, applied once per
      // rendered frame" via rAF keeps the tilt just as responsive to the
      // eye while cutting the actual DOM-write rate dramatically.
      let pendingEvent = null;
      let rafScheduled = false;

      const applyTilt = () => {
        rafScheduled = false;
        if (!pendingEvent) return;
        const rect = vial.getBoundingClientRect();
        const px = (pendingEvent.clientX - rect.left) / rect.width - 0.5;
        const py = (pendingEvent.clientY - rect.top) / rect.height - 0.5;
        vial.style.setProperty("--tilt-x", (-py * 14).toFixed(2) + "deg");
        vial.style.setProperty("--tilt-y", (px * 14).toFixed(2) + "deg");
        pendingEvent = null;
      };

      const onMove = (e) => {
        pendingEvent = e;
        if (!rafScheduled) {
          rafScheduled = true;
          requestAnimationFrame(applyTilt);
        }
      };
      const onLeave = () => {
        pendingEvent = null;
        vial.style.setProperty("--tilt-x", "0deg");
        vial.style.setProperty("--tilt-y", "0deg");
      };
      vial.addEventListener("mousemove", onMove, { passive: true });
      vial.addEventListener("mouseleave", onLeave);
    });
  }

  handleResize() {
    if (!this.renderer) return;
    const w = window.innerWidth;
    const h = window.innerHeight;
    this.renderer.setPixelRatio(computeEffectivePixelRatio());
    this.particleUniforms.uPixelRatio.value = this.renderer.getPixelRatio();
    this.renderer.setSize(w, h, false);
    this.camera.aspect = w / h;
    this.camera.updateProjectionMatrix();
    this.bgUniforms.uResolution.value.set(w, h);
  }

  handleMouseMove(e) {
    this.mouse.x = (e.clientX / window.innerWidth) * 2 - 1;
    this.mouse.y = -((e.clientY / window.innerHeight) * 2 - 1);
  }

  // A single uMorph uniform (0..2), driven by scroll progress across the
  // 3 acts and lerped every frame — this is what guarantees the
  // Clarity -> Energy -> Balance transition always reads as continuous
  // motion rather than a hard cut between scenes.
  setUpScrollMorph() {
    if (typeof window.gsap === "undefined" || typeof window.ScrollTrigger === "undefined" || !this.acts.length) {
      return;
    }
    const gsap = window.gsap;
    gsap.registerPlugin(window.ScrollTrigger);

    window.ScrollTrigger.create({
      trigger: this.root,
      start: "top top",
      end: "bottom bottom",
      onUpdate: (self) => {
        this.targetMorph = self.progress * 2;
      },
    });
  }

  animate() {
    if (this.destroyed) return;
    requestAnimationFrame(() => this.animate());

    const delta = this.clock.getDelta();
    const elapsed = this.clock.getElapsedTime();

    // Lerp toward scroll-driven target — never a jump — same principle
    // applied to morph, camera position, and camera look-at below.
    this.morph += (this.targetMorph - this.morph) * Math.min(delta * 3.0, 1.0);

    this.bgUniforms.uTime.value = elapsed;
    this.bgUniforms.uMorph.value = this.morph;
    this.particleUniforms.uTime.value = elapsed;
    this.particleUniforms.uMorph.value = this.morph;
    this.particleUniforms.uMouse.value.set(this.mouse.x * 1.4, this.mouse.y * 1.4);

    // Camera drift: lerp position toward a target that itself slowly
    // wanders with the mouse and the current act — no direct snaps.
    this.cameraTarget.set(this.mouse.x * 0.4, this.mouse.y * 0.25 + 0.1, 3 - this.morph * 0.3);
    this.camera.position.lerp(this.cameraTarget, Math.min(delta * 1.5, 1.0));
    this.cameraLookTarget.lerp(new THREE.Vector3(this.mouse.x * 0.15, this.mouse.y * 0.1, 0), Math.min(delta * 1.5, 1.0));
    this.camera.lookAt(this.cameraLookTarget);

    this.particles.rotation.y = elapsed * 0.03;

    this.renderer.autoClear = false;
    this.renderer.clear();
    this.renderer.render(this.bgScene, this.bgCamera);
    this.renderer.render(this.scene, this.camera);
  }

  destroy() {
    this.destroyed = true;
    window.removeEventListener("resize", this._onResize);
    window.removeEventListener("mousemove", this._onMouseMove);
    this.renderer && this.renderer.dispose();
  }
}

function initAll() {
  // Same principle as the other two motion scripts on this theme: never
  // force a heavy generative animation on someone who has asked their OS
  // to reduce motion. The section's headings/vials stay fully visible and
  // scrollable against the CSS gradient fallback (see the section's own
  // {% style %}) — just without the WebGL scene on top of it.
  if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;

  document.querySelectorAll("[data-cinematic-experience]").forEach((root) => {
    if (root.dataset.cinematicBound) return;
    root.dataset.cinematicBound = "true";
    new CinematicExperience(root);
  });
}

if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", initAll);
} else {
  initAll();
}
document.addEventListener("shopify:section:load", initAll);
